/*
 * This file is part of the NSR STEM Utdanning 2022 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import ExternalLink from 'components/Icons/ExternalLink/ExternalLink';
import Hamburger from 'components/Hamburger/Hamburger';
import logo from '../../../../media/gfx/xfactor-logo-black.svg';

import './Menu.scss';

/**
 * This is the Menu component.
 *
 * @author Thomas Sømoen <thomas.somoen@try.no>
 *
 * @return {JSX}
 */
const Menu = forwardRef(
  ({ open, items, stem, home, languages, labels, onClose }, ref) => {
    return (
      <nav
        ref={ref}
        id="menu"
        role="menu"
        className="layout-padding menu"
        aria-labelledby="menubutton"
      >
        <button
          className="toggle"
          role="menuitem"
          aria-label={open ? labels.close : labels.open}
          onClick={onClose}
        >
          <Hamburger open={open} />
        </button>
        <div className="languages">
          {languages.map(({ id, short, title, url, active }) => (
            <a
              key={id}
              href={url}
              title={title}
              role="menuitem"
              className={classnames({ active })}
            >
              {short}
            </a>
          ))}
        </div>
        <div className="content">
          {items.map(({ id, title, url, active }) => {
            return (
              <a
                key={id}
                href={url}
                tabIndex={open ? null : '-1'}
                role="menuitem"
                className={classnames('link-decorative blue', { active })}
              >
                {title}
              </a>
            );
          })}
        </div>
        <div className="stem">
          <a
            role="menuitem"
            className="external-link"
            href={stem.url}
            title={stem.title}
            tabIndex={open ? null : '-1'}
          >
            <span className="text">{stem.text}</span>
            <ExternalLink title={stem.title} />
          </a>
        </div>
        <div className="home">
          <a
            role="menuitem"
            href={home.url}
            title={home.title}
            tabIndex={open ? null : '-1'}
          >
            <img src={logo} width="205" height="32" alt={home.alt} />
          </a>
        </div>
      </nav>
    );
  }
);

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
Menu.propTypes = {
  open: PropTypes.bool,
  languages: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      title: PropTypes.string.isRequired,
      short: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
    })
  ).isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
      active: PropTypes.bool,
    })
  ).isRequired,
  stem: PropTypes.shape({
    url: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
  }).isRequired,
  home: PropTypes.shape({
    url: PropTypes.string.isRequired,
    alt: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
  }).isRequired,
  labels: PropTypes.shape({
    open: PropTypes.string.isRequired,
    close: PropTypes.string.isRequired,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
Menu.defaultProps = {
  open: false,
  languages: [],
  items: [],
  stem: {
    url: '',
    text: '',
    title: '',
  },
  home: {
    url: '',
    alt: '',
    title: '',
  },
  labels: {
    open: 'Open menu',
    close: 'Open menu',
  },
  onClose: () => {},
};

export default Menu;
